export type SearchResult = { type: string; name: string; url: string; image?: string; image_alt?: string };
type Product = SearchResult & { id: number; sku: string; aliases: string[]; terms: string; text: string };
type Term = SearchResult & { id: number; taxonomy: string };
export type SearchIndex = { schema: number; products: Product[]; terms: Term[] };
const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
export function matchShopSearch(data: SearchIndex, raw: string): SearchResult[] {
    const query = normalize(raw.slice(0, 100));
    if (query.length < 2) return [];
    const tokens = query.split(' ');
    const matches = data.products.flatMap(product => {
        const name = normalize(product.name);
        const skus = [product.sku, ...product.aliases].map(normalize);
        const haystack = `${name} ${skus.join(' ')} ${product.terms} ${product.text}`;
        if (!tokens.every(token => haystack.includes(token))) return [];
        const rank = skus.includes(query) ? 0 : name === query ? 1 : name.startsWith(query) ? 2 : name.includes(query) ? 3 : product.terms.includes(query) ? 4 : 5;
        return [{ product, rank }];
    }).sort((a, b) => a.rank - b.rank || a.product.id - b.product.id).slice(0, 5).map(item => item.product);
    const results: SearchResult[] = [...matches];
    for (const taxonomy of ['product_cat', 'pa_range', 'pa_key_ingredient']) {
        results.push(...data.terms.filter(term => term.taxonomy === taxonomy && tokens.every(token => normalize(term.name).includes(token)))
            .sort((a, b) => normalize(a.name) < normalize(b.name) ? -1 : normalize(a.name) > normalize(b.name) ? 1 : a.id - b.id).slice(0, 4));
    }
    return results.map(({ type, name, url, image, image_alt }) => ({ type, name, url, ...(image !== undefined ? { image, image_alt } : {}) }));
}
class ShopSearchCache {
    private data?: SearchIndex;
    private pending?: Promise<SearchIndex | undefined>;
    private version = '';
    private checkedAt = 0;
    private retryAt = 0;
    constructor(private manifestUrl: string) {}
    private async json(url: string, revalidate: boolean) {
        const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), 4000);
        try { const response = await fetch(url, { signal: controller.signal, cache: revalidate ? 'no-cache' : 'default' }); if (!response.ok) throw Error('Search index unavailable'); return await response.json(); }
        finally { clearTimeout(timer); }
    }
    preload(): Promise<SearchIndex | undefined> {
        if (this.pending) return this.pending;
        if (Date.now() < this.retryAt) return Promise.resolve(undefined);
        if (this.data && Date.now() - this.checkedAt < 60000) return Promise.resolve(this.data);
        this.pending = (async () => {
            try {
                const manifest = await this.json(this.manifestUrl, true);
                if (manifest.schema !== 1 || !/^[a-f0-9]{20}$/.test(manifest.version)) throw Error('Invalid search manifest');
                const url = new URL(manifest.url, this.manifestUrl);
                if (url.origin !== new URL(this.manifestUrl, location.href).origin) throw Error('Unexpected search origin');
                if (!this.data || this.version !== manifest.version) {
                    const data = await this.json(url.href, false) as SearchIndex;
                    const valid = (item: SearchResult & { id: number }) => Number.isSafeInteger(item.id) && typeof item.name === 'string' && typeof item.type === 'string' && typeof item.url === 'string' && /^https?:\/\//.test(item.url);
                    if (data.schema !== 1 || !Array.isArray(data.products) || !Array.isArray(data.terms)
                        || data.products.some(p => !valid(p) || typeof p.sku !== 'string' || !Array.isArray(p.aliases) || p.aliases.some(s => typeof s !== 'string') || typeof p.terms !== 'string' || typeof p.text !== 'string')
                        || data.terms.some(t => !valid(t) || typeof t.taxonomy !== 'string')) throw Error('Invalid search index');
                    this.data = data; this.version = manifest.version;
                }
                this.checkedAt = Date.now(); return this.data;
            } catch { this.data = undefined; this.retryAt = Date.now() + 5000; return undefined; }
            finally { this.pending = undefined; }
        })();
        return this.pending;
    }
    async search(query: string): Promise<SearchResult[] | undefined> { const data = await this.preload(); return data ? matchShopSearch(data, query) : undefined; }
}
const caches = new Map<string, ShopSearchCache>();
export function getShopSearchCache(url?: string) {
    if (!url) return undefined;
    if (!caches.has(url)) caches.set(url, new ShopSearchCache(url));
    return caches.get(url)!;
}
